/**
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2021, 2022
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'underscore',
	'dashboard-analytics/widgets/livewidget/nls/StringResources',
	'dashboard-analytics/util/FontFamilyOptions',
	'dashboard-analytics/util/FontUtils',
	'../../../lib/@waca/dashboard-common/dist/utils/TextEditorConstants'
], function(_, StringResources, fontFamilyOptions, FontUtils, TextEditorConstants) {
	'use strict';

	/*
	* The purpose of this object is to provide a singleton that can create
	* the font property layout for VIPR visualizations and customVis consumed by the properties panel.
	*/
	const FontPropertiesCreator = (() => {

		/**
		 * Instance stores a reference to the Singleton
		 * @type {object}
		 */
		let instance = null;
		let fontSizeOptions = null;

		function init() {
			return {

				/**
				* @returns the font group layout information in a format consumable by the FontCombo control in the
				* properties panel
				* UX Carbon design displays font-family, font-size and text color information on the same line, so this layout will have
				* information of these 3 properties
				* For customVis or V1 normal vis, this class generates  the font layout only for font-family and font-size (color property is not grouped in this layout)
				*/
				createFontGroupPropertyLayout: function(options) {
					const property = options.property;
					const { content, dashboard, properties, viprUtils,  viprConfig, sectionId } = options.context;
					const _fontFamilyOptions = [...fontFamilyOptions];
					_fontFamilyOptions.unshift({ value: 'auto', label: StringResources.get('automaticTypeCaption') });

					const fontFamilyInfo = {
						id: `font.xdashboardx.${property.id}`,
						value: viprUtils.getVidaFontPropertiesPart('family', content.getPropertyValue(property.id)) || StringResources.get('automaticTypeCaption'),
						onChange: instance._getSetFontProperty_V2('font', property.id, content, viprUtils, dashboard, viprConfig),
						options: _fontFamilyOptions
					};

					const fontSizeInfo = {
						id: `fontSize.xdashboardx.${property.id}`,
						value: viprUtils.getVidaFontPropertiesPart('size', content.getPropertyValue(property.id)) || StringResources.get('automaticTypeCaption'),
						onChange: instance._getSetFontProperty_V2('fontSize', property.id, content, viprUtils, dashboard, viprConfig),
						options: instance._getFontSizeOptions()
					};

					// Find the text color property that is grouped with this font
					const colorPropId = viprConfig.getColorPropertyAssociatedWithFont(property.id);
					const colorProperty = properties.find(prop => prop.id === colorPropId);
					let textColorInfo;
					if (colorProperty && colorProperty.public) {
						const colorPropDesc = Object.assign({}, colorProperty);
						const colorsList = dashboard.getFeature('Colors').getDashboardColorSet();

						textColorInfo = {
							id: colorPropId,
							addButton: false,
							paletteTitle: StringResources.get('colorPaletteTitle'),
							paletteType: 'DashboardColorSet',
							value: colorPropDesc.value == null ? colorsList[0].id : colorPropDesc.value,
							onChange: colorPropDesc.onChange
						};
					}

					const fontLayout = {
						id: `${property.id}.fontCombo`,
						editor: {
							sectionId: sectionId,
							position: property.displayPos,
							uiControl: {
								type: 'FontCombo',
								fontFamily: fontFamilyInfo,
								fontSize: fontSizeInfo,
								textColor: textColorInfo // optional, could be undefined
							}
						}
					};
					return [fontLayout];
				},

				// This method generates the uiControl (layout) for font-family and font-size for customVis and normal vis V1
				createFontPropertyLayout(options) {
					const { content, dashboard, viprUtils,  isCustomVis, sectionId, viprConfig } = options.context;
					const property = options.property;
					let customFonts = dashboard.getFeature('DashboardSettings').get('fonts') || [];
					customFonts = FontUtils.normalizeFonts(customFonts);

					const _fontFamilyOptions = [...customFonts, ...fontFamilyOptions];
					_fontFamilyOptions.unshift({ value: 'auto', label: StringResources.get('automaticTypeCaption') });

					const fontFamily = {
						id: `font.xdashboardx.${property.id}`,
						editor: {
							position: property.displayPos,
							sectionId: sectionId,
							uiControl: instance._getPropertyUIControl({
								name: `font.xdashboardx.${property.id}`,
								active: true,
								options: _fontFamilyOptions,
								public: true,
								ariaLabel: StringResources.get('fontPropLabel'),
								label:  isCustomVis && property.label ? `${property.label} ${StringResources.get('fontFamily')}` : StringResources.get('fontPropLabel'),
								type:'DropDown',
								onChange: instance._getSetFontProperty('font', property.id, content, viprUtils, dashboard, viprConfig),
								value: viprUtils.getVidaFontPropertiesPart('family', content.getPropertyValue(property.id)) || StringResources.get('automaticTypeCaption')
							})
						}
					};

					const fontSize = {
						id: `fontSize.xdashboardx.${property.id}`,
						editor: {
							position: property.displayPos + 1,
							sectionId: sectionId,
							uiControl: instance._getPropertyUIControl(_.extend(property, {
								ariaLabel: StringResources.get('fontSizePropLabel'),
								label:  isCustomVis && property.label ? `${property.label} ${StringResources.get('fontSize')}` : StringResources.get('fontSizePropLabel'),
								type:'DropDown',
								options:  instance._getFontSizeOptions(),
								onChange: instance._getSetFontProperty('fontSize', property.id, content, viprUtils, dashboard, viprConfig),
								value: viprUtils.getVidaFontPropertiesPart('size', content.getPropertyValue(property.id)) || StringResources.get('automaticTypeCaption')
							}))
						}
					};
					return [fontFamily, fontSize];
				},

				_getPropertyUIControl: function(property) {
					// exclude all general and layout properties
					return _.omit(property, 'id', 'public', 'displayPos', 'tabId', 'tabName', 'sectionPosition', 'sectionId', 'sectionName');
				},

				_getSetFontProperty_V2: function(fontPart, propertyID, content, viprUtils, dashboard, viprConfig) {
					const onChangeFontCallBack = instance._onChangeFontProperty.bind(instance);
					return function(value, transactionToken) {
						onChangeFontCallBack({ fontPart, propertyID, content, viprUtils, dashboard, viprConfig, value, transactionToken });
					};
				},

				_onChangeFontProperty: function({ fontPart, propertyID, content, viprUtils, viprConfig, value, transactionToken }) {
					const groupedProperty = content.getFeature('Visualization').getDefinition().getProperty('groupedProperty') || {};
					const propertyNameList = content.getPropertyNameList();

					if(value === 'auto'){
						value = null;
					}
					const fontProperty = viprUtils.buildVidaFontPropertiesFromParts({ [fontPart]: value }, content.getPropertyValue(propertyID));

					const propertyList = [ propertyID ];

					if(viprConfig.isInAllowedGroups(propertyID)) {
						_.each(groupedProperty[propertyID], prop => {
							if(propertyNameList.indexOf(prop) !== -1){
								propertyList.push(prop);
							}
						});
					}

					propertyList.forEach(prop => content.setPropertyValue(prop, fontProperty, transactionToken));
				},

				_getSetFontProperty: function(fontPart, propertyID, content, viprUtils, dashboard, viprConfig){
					const onChangeFontCallBack = instance._onChangeFontProperty.bind(instance);
					// The argument pseudoPropertyId is passed to this function from PropertyListUtil.customOnChangeCallback
					return function(pseudoPropertyId, value, transactionToken){
						onChangeFontCallBack({ fontPart, propertyID, content, viprUtils, dashboard, viprConfig, value, transactionToken });
					};
				},

				_getFontSizeOptions: function(){
					if (!fontSizeOptions){
						fontSizeOptions =  TextEditorConstants.FONT_SIZE_CHOICES.map((option) => {
							return {
								'value' : option.value === 'auto' ? option.value : option.value +  'px',
								'label': option.label
							};
						});
					}
					return fontSizeOptions;
				}
			};
		}

		return {

			// Get the Singleton instance if one exists
			// or create one if it doesn't
			getInstance: function () {

				if (!instance) {
					instance = init();
				}

				return instance;
			}
		};

	})();
	return FontPropertiesCreator;
});
